import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, UserPlus, Check, ArrowLeft } from "lucide-react";
import { ClientForm, EMPTY_CLIENT } from "@/components/ClientForm";
import { toast } from "sonner";

export const ClientStep = ({ clientId, onSelect }) => {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_CLIENT);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)).catch(() => {}); }, []);

  const norm = (s) => (s || "").toString().toLowerCase().replace(/\D/g, "");
  const shown = clients.filter((c) => !q || `${c.name} ${c.code}`.toLowerCase().includes(q.toLowerCase()) || (norm(q) && (norm(c.phone).includes(norm(q)) || norm(c.whatsapp).includes(norm(q)) || norm(c.document).includes(norm(q))))).slice(0, 8);
  const selected = clients.find((c) => c.id === clientId);

  const createClient = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome do cliente"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/clients", form);
      setClients((l) => [data, ...l]); setCreating(false); setForm(EMPTY_CLIENT);
      onSelect(data); toast.success("Cliente cadastrado!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar cliente"); } finally { setSaving(false); }
  };

  if (creating) return (
    <div className="space-y-4">
      <button type="button" onClick={() => setCreating(false)} className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="wizard-client-back"><ArrowLeft className="h-4 w-4" /> Voltar para a busca</button>
      <ClientForm form={form} setForm={setForm} />
      <Button className="w-full h-12 text-base" onClick={createClient} disabled={saving} data-testid="wizard-client-create"><Check className="h-5 w-5 mr-1" /> {saving ? "Salvando..." : "Salvar e usar este cliente"}</Button>
    </div>
  );

  return (
    <div className="space-y-4">
      {selected && (
        <div className="rounded-xl border-2 border-primary bg-primary/5 p-4" data-testid="wizard-client-selected">
          <p className="text-xs text-primary font-semibold">Cliente selecionado</p>
          <p className="font-display font-bold text-lg">{selected.name}</p>
          <p className="text-sm text-muted-foreground">{[selected.phone || selected.whatsapp, selected.document, [selected.address, selected.number].filter(Boolean).join(", "), selected.neighborhood, selected.city].filter(Boolean).join(" • ")}</p>
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Digite nome, telefone ou CPF" className="pl-11 h-14 text-base" data-testid="wizard-client-search" autoFocus={!selected} />
      </div>
      <div className="space-y-2">
        {shown.map((c) => (
          <button key={c.id} type="button" onClick={() => onSelect(c)} data-testid={`wizard-client-${c.id}`}
            className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 transition-colors active:scale-[0.99] ${c.id === clientId ? "border-primary bg-primary/5" : "border-border hover:border-primary"}`}>
            <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0">{c.name.slice(0, 1).toUpperCase()}</div>
            <div className="min-w-0 flex-1"><p className="font-semibold truncate">{c.name}</p><p className="text-xs text-muted-foreground truncate">{[c.phone || c.whatsapp, c.document, c.city].filter(Boolean).join(" • ") || c.code}</p></div>
            {c.id === clientId && <Check className="h-5 w-5 text-primary" />}
          </button>
        ))}
        {clients.length > 0 && shown.length === 0 && <p className="text-sm text-muted-foreground text-center py-2">Nenhum cliente encontrado para "{q}".</p>}
      </div>
      <Button variant="outline" className="w-full h-12 text-base border-dashed" onClick={() => { setForm({ ...EMPTY_CLIENT, name: /\d/.test(q) ? "" : q, phone: /\d/.test(q) ? q : "" }); setCreating(true); }} data-testid="wizard-client-new">
        <UserPlus className="h-5 w-5 mr-2" /> Cadastrar novo cliente
      </Button>
    </div>
  );
};
